import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Save, RotateCcw } from "lucide-react";

const CHAT_PLACEHOLDERS = ["{role_label}", "{background}", "{tone}", "{language}", "{attached_files}"];
const SUMMARY_PLACEHOLDERS = ["{filename}", "{kind}", "{chunks}"];
const INSPECT_PLACEHOLDERS = ["{app_name}", "{filename}", "{kind}", "{language}", "{tone}", "{file_content}"];
const TITLE_PLACEHOLDERS = ["{app_name}", "{language}", "{conversation_excerpt}"];

const EMPTY = {
  chat_template: "", voice_template: "", summary_template: "", inspect_template: "",
  title_template: "",
  skip_low_balance_message_es: "", skip_low_balance_message_en: "",
  skip_no_content_message_es: "", skip_no_content_message_en: "",
  skip_already_inspected_label_es: "", skip_already_inspected_label_en: "",
};

export default function PromptsTab() {
  const [draft, setDraft] = useState(EMPTY);
  const [original, setOriginal] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const set = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  const load = async () => {
    try {
      const { data } = await api.get("/admin/prompts");
      const merged = { ...EMPTY, ...data };
      setDraft(merged);
      setOriginal(merged);
    } catch {
      toast.error("Failed to load prompts");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/prompts", draft);
      const merged = { ...EMPTY, ...data };
      setDraft(merged);
      setOriginal(merged);
      toast.success("Prompts saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (!window.confirm("Reset ALL prompts on this page to built-in defaults?")) return;
    const DEFAULTS = {
      chat_template: `You are Nexa, a helpful AI assistant.

User profile:
- Role: {role_label}
- Background: {background}
- Tone preference: {tone}
- Language: {language}
Adapt your terminology and explanations to fit this user.
{attached_files}`,
      voice_template: `You are Nexa, speaking out loud through a text-to-speech engine. Every character of your output is read aloud verbatim. Speak like a human, not a written report.

User profile:
- Role: {role_label}
- Background: {background}
- Tone preference: {tone}
- Language: {language}
Adapt your terminology and tone to fit this user.
{attached_files}`,
      summary_template: `You are summarising a document a user attached to their AI conversation.
Filename: {filename}
Type: {kind}
Content excerpt:
---
{chunks}
---
Write a brief, factual description of what this attachment contains in NO MORE than 100 words. Focus on the subject matter, document type, and key topics. Use plain prose only — no bullets, no markdown, no headings, no preamble like 'This document'. Start directly with the content description.`,
      inspect_template: `You are {app_name}. The user just attached the file "{filename}" ({kind}). Without being asked, do exactly two things:

1. **What is this?** — Summarise the document in 1–2 sentences.
2. **What does it contain?** — A short bullet list of the key data (entities, dates, amounts, sections, etc.).

Answer in {language}, in a {tone} tone. If the extraction is empty or unreadable, say so plainly — never invent content.

Extracted content:
{file_content}`,
      title_template: `You are {app_name}. Generate a short title (3 to 5 words, 40 characters max) that summarises this conversation. No quotes, no emojis, no trailing punctuation. Reply only with the title, in {language}.

Conversation:
{conversation_excerpt}`,
      skip_low_balance_message_es: "Tu saldo de tokens es bajo. Recargá tus tokens para que pueda analizar este archivo.",
      skip_low_balance_message_en: "Your token balance is too low. Top up to let me analyze this file.",
      skip_no_content_message_es: "No pudimos leer contenido legible en este archivo.",
      skip_no_content_message_en: "We couldn't read any legible content in this file.",
      skip_already_inspected_label_es: "Ya analizado en una conversación anterior.",
      skip_already_inspected_label_en: "Already analyzed in a previous conversation.",
    };
    setDraft(DEFAULTS);
    toast.message("Defaults loaded — click Save to apply.");
  };

  const dirty = Object.keys(EMPTY).some((k) => draft[k] !== original[k]);

  return (
    <div>
      <div className="border border-border bg-card p-6">
        <h3 className="font-display font-bold tracking-tight text-lg">System prompt templates</h3>
        <p className="text-sm text-muted-foreground mt-1">
          These prompts are sent to <strong>every</strong> LLM call. Use the placeholders below — they're substituted at runtime
          with the active user's onboarding profile and any attached files.
        </p>
      </div>

      <Tabs defaultValue="chat" className="mt-6">
        <TabsList className="rounded-sm flex-wrap h-auto">
          <TabsTrigger value="chat" data-testid="prompts-chat-tab" className="rounded-sm">Chat template</TabsTrigger>
          <TabsTrigger value="voice" data-testid="prompts-voice-tab" className="rounded-sm">Voice template</TabsTrigger>
          <TabsTrigger value="summary" data-testid="prompts-summary-tab" className="rounded-sm">Attachment summary</TabsTrigger>
          <TabsTrigger value="inspect" data-testid="prompts-inspect-tab" className="rounded-sm">Auto-inspect</TabsTrigger>
          <TabsTrigger value="title" data-testid="prompts-title-tab" className="rounded-sm">Title generator</TabsTrigger>
          <TabsTrigger value="skip" data-testid="prompts-skip-tab" className="rounded-sm">Skip notices</TabsTrigger>
        </TabsList>

        <TabsContent value="chat" className="mt-4">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {CHAT_PLACEHOLDERS.map((p) => (
              <code key={p} className="text-xs font-mono bg-secondary px-2 py-0.5 rounded-sm border border-border">{p}</code>
            ))}
          </div>
          <Label className="text-xs uppercase tracking-wider font-mono">Chat system prompt</Label>
          <Textarea
            value={draft.chat_template}
            onChange={(e) => set("chat_template", e.target.value)}
            rows={16}
            className="mt-1.5 rounded-sm font-mono text-sm"
            data-testid="prompts-chat-textarea"
          />
          <p className="text-xs text-muted-foreground mt-1.5 font-mono">
            Sent on every text chat message. Keep placeholders so user context + file content get injected.
          </p>
        </TabsContent>

        <TabsContent value="voice" className="mt-4">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {CHAT_PLACEHOLDERS.map((p) => (
              <code key={p} className="text-xs font-mono bg-secondary px-2 py-0.5 rounded-sm border border-border">{p}</code>
            ))}
          </div>
          <Label className="text-xs uppercase tracking-wider font-mono">Voice "instructions" prompt</Label>
          <Textarea
            value={draft.voice_template}
            onChange={(e) => set("voice_template", e.target.value)}
            rows={16}
            className="mt-1.5 rounded-sm font-mono text-sm"
            data-testid="prompts-voice-textarea"
          />
          <p className="text-xs text-muted-foreground mt-1.5 font-mono">
            Sent as the OpenAI Realtime session "instructions". Keep it short and conversational — the model speaks this aloud.
          </p>
        </TabsContent>

        <TabsContent value="summary" className="mt-4">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {SUMMARY_PLACEHOLDERS.map((p) => (
              <code key={p} className="text-xs font-mono bg-secondary px-2 py-0.5 rounded-sm border border-border">{p}</code>
            ))}
          </div>
          <Label className="text-xs uppercase tracking-wider font-mono">Attachment summary prompt</Label>
          <Textarea
            value={draft.summary_template}
            onChange={(e) => set("summary_template", e.target.value)}
            rows={16}
            className="mt-1.5 rounded-sm font-mono text-sm"
            data-testid="prompts-summary-textarea"
          />
          <p className="text-xs text-muted-foreground mt-1.5 font-mono">
            Runs against the <strong>default model alias</strong> after each file is processed by Tensornex. Output is hard-capped at 100 words server-side. Used in My Storage hover-cards.
          </p>
        </TabsContent>

        <TabsContent value="inspect" className="mt-4">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {INSPECT_PLACEHOLDERS.map((p) => (
              <code key={p} className="text-xs font-mono bg-secondary px-2 py-0.5 rounded-sm border border-border">{p}</code>
            ))}
          </div>
          <Label className="text-xs uppercase tracking-wider font-mono">Auto-inspect prompt</Label>
          <Textarea
            value={draft.inspect_template}
            onChange={(e) => set("inspect_template", e.target.value)}
            rows={16}
            className="mt-1.5 rounded-sm font-mono text-sm"
            data-testid="prompts-inspect-textarea"
          />
          <p className="text-xs text-muted-foreground mt-1.5 font-mono">
            Fires <strong>once per (user, file)</strong> when a file is attached. On reattachment in a different conversation, Faro re-shows the prior analysis (no LLM cost). Max chunks injected is set in Admin → Billing → Token value.
          </p>
        </TabsContent>

        <TabsContent value="title" className="mt-4">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {TITLE_PLACEHOLDERS.map((p) => (
              <code key={p} className="text-xs font-mono bg-secondary px-2 py-0.5 rounded-sm border border-border">{p}</code>
            ))}
          </div>
          <Label className="text-xs uppercase tracking-wider font-mono">Conversation title prompt</Label>
          <Textarea
            value={draft.title_template}
            onChange={(e) => set("title_template", e.target.value)}
            rows={10}
            className="mt-1.5 rounded-sm font-mono text-sm"
            data-testid="prompts-title-textarea"
          />
          <p className="text-xs text-muted-foreground mt-1.5 font-mono">
            Runs once per conversation, after the first message or auto-inspection lands. Generates the title shown in the sidebar. Disable globally from <strong>Admin → Billing → Token value</strong>.
          </p>
        </TabsContent>

        <TabsContent value="skip" className="mt-4 space-y-5">
          <p className="text-xs text-muted-foreground font-mono">
            These short notices are shown inside the chat when an attachment can't be auto-inspected. The Spanish copy is used for locales starting with <code>es</code>; English is used everywhere else.
          </p>

          <SkipPair
            titleEs="Saldo bajo (es-AR)" titleEn="Low balance (English)"
            valueEs={draft.skip_low_balance_message_es}
            valueEn={draft.skip_low_balance_message_en}
            onEs={(v) => set("skip_low_balance_message_es", v)}
            onEn={(v) => set("skip_low_balance_message_en", v)}
            testId="skip-low-balance"
          />
          <SkipPair
            titleEs="Sin contenido legible (es-AR)" titleEn="No readable content (English)"
            valueEs={draft.skip_no_content_message_es}
            valueEn={draft.skip_no_content_message_en}
            onEs={(v) => set("skip_no_content_message_es", v)}
            onEn={(v) => set("skip_no_content_message_en", v)}
            testId="skip-no-content"
          />
          <SkipPair
            titleEs="Ya analizado antes — etiqueta (es-AR)" titleEn="Already analyzed — label (English)"
            valueEs={draft.skip_already_inspected_label_es}
            valueEn={draft.skip_already_inspected_label_en}
            onEs={(v) => set("skip_already_inspected_label_es", v)}
            onEn={(v) => set("skip_already_inspected_label_en", v)}
            testId="skip-already-inspected"
            small
          />
        </TabsContent>
      </Tabs>

      <div className="mt-6 flex items-center gap-3">
        <Button onClick={save} disabled={saving || !dirty} className="rounded-sm" data-testid="prompts-save-button">
          <Save className="h-4 w-4 mr-1.5" />
          {saving ? "Saving…" : dirty ? "Save prompts" : "Saved"}
        </Button>
        <Button variant="outline" onClick={resetToDefaults} className="rounded-sm" data-testid="prompts-reset-button">
          <RotateCcw className="h-4 w-4 mr-1.5" /> Load defaults
        </Button>
        <span className="text-xs text-muted-foreground font-mono">
          Changes take effect on the next chat / voice call / file upload.
        </span>
      </div>
    </div>
  );
}

function SkipPair({ titleEs, titleEn, valueEs, valueEn, onEs, onEn, testId, small }) {
  const Editor = small ? Input : Textarea;
  const extraProps = small ? {} : { rows: 2 };
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <Label className="text-xs uppercase tracking-wider font-mono">{titleEs}</Label>
        <Editor
          value={valueEs}
          onChange={(e) => onEs(e.target.value)}
          className="mt-1.5 rounded-sm font-mono text-sm"
          data-testid={`${testId}-es`}
          {...extraProps}
        />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-mono">{titleEn}</Label>
        <Editor
          value={valueEn}
          onChange={(e) => onEn(e.target.value)}
          className="mt-1.5 rounded-sm font-mono text-sm"
          data-testid={`${testId}-en`}
          {...extraProps}
        />
      </div>
    </div>
  );
}
